import { supabase } from '@/lib/supabase';
import { getOrSetCache } from '@/lib/coreCache';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — Catalogue des entités centrales Produit & Service
// Champs transverses utilisés par tous les modules (Commerce, POS,
// Inventory, Orders, Marketing, Finance…). Ce fichier centralise les
// libellés et petits utilitaires pour rester cohérent partout.
// ─────────────────────────────────────────────────────────────

// ── Commissions employés (socle RH relié aux commandes) ──

export interface CommissionTypeDef {
  key: string;
  label: string;
  suffix: string;
  icon: string;
}

export const COMMISSION_TYPES: CommissionTypeDef[] = [
  { key: 'percentage', label: 'Pourcentage du montant', suffix: '%', icon: 'ri-percent-line' },
  { key: 'fixed', label: 'Montant fixe par vente', suffix: '', icon: 'ri-money-dollar-circle-line' },
];

export function commissionTypeMeta(key: string | null | undefined): CommissionTypeDef {
  return COMMISSION_TYPES.find((c) => c.key === key) || COMMISSION_TYPES[0];
}

/** Calcule la commission d'une vente selon le type et la valeur configurés. */
export function computeCommission(
  type: string | null | undefined,
  value: number | null | undefined,
  total: number,
): number {
  const v = Number(value ?? 0) || 0;
  if (v <= 0) return 0;
  if (type === 'fixed') return Math.round(v * 100) / 100;
  return Math.round(((total * v) / 100) * 100) / 100;
}

// ── Visibilité produit ──

export interface VisibilityDef {
  key: string;
  label: string;
  icon: string;
}

export const PRODUCT_VISIBILITIES: VisibilityDef[] = [
  { key: 'public', label: 'Public', icon: 'ri-eye-line' },
  { key: 'private', label: 'Privé (non listé)', icon: 'ri-eye-off-line' },
  { key: 'hidden', label: 'Masqué', icon: 'ri-lock-line' },
];

export function visibilityLabel(key: string | null | undefined): string {
  return (PRODUCT_VISIBILITIES.find((v) => v.key === key) || PRODUCT_VISIBILITIES[0]).label;
}

// ── Configuration de taxe (simple) ──

export interface TaxConfig {
  rate?: number;
  included?: boolean;
}

export function normalizeTaxConfig(raw: unknown): TaxConfig {
  if (!raw || typeof raw !== 'object') return { rate: 0, included: false };
  const obj = raw as Record<string, unknown>;
  const rate = Number(obj.rate ?? 0);
  return { rate: Number.isFinite(rate) ? rate : 0, included: Boolean(obj.included) };
}

// ── Type de lieu d'un service ──

export interface LocationTypeDef {
  key: string;
  label: string;
  icon: string;
}

export const SERVICE_LOCATION_TYPES: LocationTypeDef[] = [
  { key: 'onsite', label: 'Sur place', icon: 'ri-store-2-line' },
  { key: 'remote', label: 'À distance', icon: 'ri-computer-line' },
  { key: 'client', label: 'Chez le client', icon: 'ri-home-4-line' },
  { key: 'hybrid', label: 'Hybride', icon: 'ri-shuffle-line' },
];

export function locationTypeMeta(key: string | null | undefined): LocationTypeDef {
  return SERVICE_LOCATION_TYPES.find((t) => t.key === key) || SERVICE_LOCATION_TYPES[0];
}

// ── Statut & durée d'un service ──

export const SERVICE_STATUSES: { key: string; label: string; className: string }[] = [
  { key: 'active', label: 'Actif', className: 'bg-accent-100 text-accent-900' },
  { key: 'inactive', label: 'Inactif', className: 'bg-foreground-100 text-foreground-600' },
];

export function serviceStatusMeta(key: string | null | undefined) {
  return SERVICE_STATUSES.find((s) => s.key === key) || SERVICE_STATUSES[0];
}

/** Durées proposées (en minutes). */
export const SERVICE_DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240];

export function formatDuration(minutes: number | null | undefined): string {
  const m = Number(minutes ?? 0) || 0;
  if (m <= 0) return '—';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} h` : `${h} h ${rest}`;
}

// ─────────────────────────────────────────────────────────────
// Catalogue — lecture « à travers » la couche de cache (spec 65)
// Les modules lourds (vitrine, POS, Inventory) ne recalculent pas la liste
// des produits/services à chaque visite : ils la lisent depuis le cache.
// Toute écriture (product.created/updated, service.*) invalide le cache via
// coreEventHooks — jamais de catalogue périmé.
// ─────────────────────────────────────────────────────────────

/** Produits actifs du commerce, servis depuis le cache tenant-aware. */
export async function fetchCatalogProductsCached(input: {
  organizationId: number;
  tenantId?: number | null;
  commerceId?: number | null;
  ttlSeconds?: number;
}): Promise<Record<string, unknown>[]> {
  return getOrSetCache<Record<string, unknown>[]>({
    organizationId: input.organizationId,
    tenantId: input.tenantId ?? null,
    key: `catalog:products:${input.commerceId ?? 'all'}`,
    scope: 'catalog',
    tags: ['catalog', 'products'],
    ttlSeconds: input.ttlSeconds ?? 900,
    loader: async () => {
      let query = supabase.from('product_items').select('*').eq('status', 'active');
      if (input.commerceId) query = query.eq('idcommerce', input.commerceId);
      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as Record<string, unknown>[];
    },
  });
}

/** Services du commerce, servis depuis le cache tenant-aware. */
export async function fetchCatalogServicesCached(input: {
  organizationId: number;
  tenantId?: number | null;
  commerceId?: number | null;
  ttlSeconds?: number;
}): Promise<Record<string, unknown>[]> {
  return getOrSetCache<Record<string, unknown>[]>({
    organizationId: input.organizationId,
    tenantId: input.tenantId ?? null,
    key: `catalog:services:${input.commerceId ?? 'all'}`,
    scope: 'catalog',
    tags: ['catalog', 'services'],
    ttlSeconds: input.ttlSeconds ?? 900,
    loader: async () => {
      let query = supabase.from('services').select('*');
      if (input.commerceId) query = query.eq('idvendeur', input.commerceId);
      const { data, error } = await query.order('id', { ascending: false });
      if (error) throw error;
      return (data || []) as Record<string, unknown>[];
    },
  });
}